import { useState } from "react";
import { Sparkles, ShieldCheck, Loader2, Users, Ban, Languages, Route, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const EXAMPLE = "Find the best old leads to reactivate this week, exclude anyone we can't contact, build English and Spanish outreach, and prepare the follow-up sequence.";

export default function GrowthAIOperator({ onDraftCreated }) {
  const [request, setRequest] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);

  const createDraft = async () => {
    const text = request.trim();
    if (!text) {
      toast.error("Describe the campaign you want Fortunate Ops to build.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/ai/create-campaign", { request: text });
      setDraft(data.campaign || null);
      toast.success("AI campaign draft created. No outbound was sent.");
      onDraftCreated?.(data.campaign);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create the AI campaign draft.");
    } finally {
      setBusy(false);
    }
  };

  const stats = draft?.audience_stats || {};

  return (
    <section className="mb-5 rounded-xl border border-[#B28A3D]/35 bg-white p-5 shadow-sm" data-testid="growth-ai-operator">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#B28A3D]" />
            <h2 className="font-heading text-lg font-bold">Growth AI Operator</h2>
            <Badge variant="outline">Human approval required</Badge>
          </div>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Tell Fortunate Ops the outcome you want. It will build a real saved draft, finalize the eligible audience,
            apply contact suppression and deduplication, and prepare English/Spanish content and the follow-up sequence.
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <textarea
          value={request}
          onChange={(e) => setRequest(e.target.value)}
          placeholder={EXAMPLE}
          data-testid="growth-ai-request"
          className="min-h-24 w-full rounded-lg border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-[#17324D]/20"
        />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            Draft only. No messages, calls, ads, or spending are launched from this action.
          </p>
          <Button onClick={createDraft} disabled={busy || !request.trim()} className="bg-[#17324D] text-white hover:bg-[#22456b]" data-testid="growth-ai-create">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4 text-[#D7B76E]" />}
            Create AI Draft
          </Button>
        </div>
      </div>

      {draft && (
        <div className="mt-5 space-y-4 rounded-lg border bg-slate-50/70 p-4" data-testid="growth-ai-draft-result">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-heading font-semibold">{draft.name}</h3>
            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Pending approval</Badge>
            <Badge variant="outline">Inactive</Badge>
          </div>
          <p className="text-sm text-muted-foreground">{draft.objective}</p>

          <div className="grid gap-2 sm:grid-cols-3">
            <Metric icon={Users} label="Eligible" value={stats.eligible ?? 0} />
            <Metric icon={Ban} label="Excluded" value={stats.excluded_total ?? 0} />
            <Metric icon={ShieldCheck} label="Consent review" value={stats.needs_consent_review ?? 0} />
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <InfoBlock icon={Languages} title="English outreach" text={draft.content?.en || "Not generated for this campaign type."} />
            <InfoBlock icon={Languages} title="Spanish outreach" text={draft.content?.es || "Not generated for this campaign type."} />
          </div>

          <div className="grid gap-3 lg:grid-cols-3">
            <div className="rounded-lg bg-white p-3 text-xs">
              <div className="mb-2 font-semibold">Sequence & cadence</div>
              {(draft.sequence || []).map((s) => (
                <div key={s.step} className="mb-1 text-muted-foreground">Day {s.day}: {s.channel} · {s.template_key?.replaceAll("_", " ")}</div>
              ))}
              <div className="mt-2 text-muted-foreground">{draft.cadence?.touches || 0} touches / {draft.cadence?.window_days || 0} days</div>
            </div>
            <div className="rounded-lg bg-white p-3 text-xs">
              <div className="mb-2 flex items-center gap-1.5 font-semibold"><Route className="h-3.5 w-3.5" /> Routing</div>
              {Object.entries(draft.routing || {}).slice(0, 6).map(([k, v]) => (
                <div key={k} className="mb-1 text-muted-foreground"><span className="font-medium text-foreground">{k.replaceAll("_", " ")}:</span> {v}</div>
              ))}
            </div>
            <div className="rounded-lg bg-white p-3 text-xs">
              <div className="mb-2 flex items-center gap-1.5 font-semibold"><BarChart3 className="h-3.5 w-3.5" /> KPIs</div>
              {(draft.kpis || []).map((k) => <div key={k} className="mb-1 text-muted-foreground">• {k}</div>)}
            </div>
          </div>

          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <strong>Saved draft only.</strong> Approval state is <code>pending_approval</code> and auto-launch is off. Controlled launch/send plumbing remains a separate feature and is not bypassed here.
          </div>
        </div>
      )}
    </section>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-lg bg-white p-3">
      <Icon className="h-4 w-4 text-[#315A7D]" />
      <div><div className="text-lg font-bold">{value}</div><div className="text-[11px] text-muted-foreground">{label}</div></div>
    </div>
  );
}

function InfoBlock({ icon: Icon, title, text }) {
  return (
    <div className="rounded-lg bg-white p-3 text-xs">
      <div className="mb-2 flex items-center gap-1.5 font-semibold"><Icon className="h-3.5 w-3.5" /> {title}</div>
      <p className="whitespace-pre-wrap leading-relaxed text-muted-foreground">{text}</p>
    </div>
  );
}
